import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemePageList from 'assets/components/page/themes/Theme.Page.List';
import Pagination from 'assets/components/pagination/Pagination';
import ThemePaginationRounded from 'assets/components/pagination/themes/Theme.Pagination.Rounded';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import React from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import {
  FinanceClaimJobPaymentItemInfoTab,
  useClaimJobPaymentItemProvider,
} from '../Finance.ClaimJobPaymentItem.Hooks';
import ClaimJobPaymentItemFilters from './Finance.ClaimJobPaymentItem.Filters';
import ClaimJobPaymentItemInfo from './Finance.ClaimJobPaymentItem.Info';
import ClaimJobPaymentItemActions from './Finance.ClaimJobPaymentItem.Actions';
import Box from 'assets/components/box/Box';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export default function ClaimJobPaymentItemList() {
  const { lang, translate } = getLocales();
  const { listApi, pageRouter, selectedItems, permissions, toggleItem, paymentCount, toggleSelectAll } =
    useClaimJobPaymentItemProvider();

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.claimsPaymentPending}
      render={lang.noInfo}
    />
  ) : (
    <Page
      class={ThemePageList}
      sidebar={<ClaimJobPaymentItemInfo />}
      isLoading={listApi.isExecuting}
      header={
        <>
          <h2 style={{ marginRight: 'auto' }} children={lang.claimsPaymentPending} />
          <Box class={ThemeWrapperStack}>
            {isMobile && <ClaimJobPaymentItemFilters />}
            <ClaimJobPaymentItemActions key="actions" />
          </Box>
        </>
      }
      subHeader={isDesktop && <ClaimJobPaymentItemFilters />}
      render={
        <table key="list" className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>
                {listApi.payload?.count > 0 && (
                  <Button
                    class={ThemeButtonCircle}
                    media={
                      selectedItems?.length === paymentCount
                        ? 'fas-check-square'
                        : selectedItems?.length !== paymentCount
                          ? 'fas-minus-square'
                          : 'far-square'
                    }
                    data={!(selectedItems?.length === paymentCount)}
                    onClick={toggleSelectAll}
                    active={selectedItems?.length === paymentCount}
                    htmlElementProps={{ title: lang.selectAll }}
                  />
                )}
              </th>
              <th>{lang.jobNumber}</th>
              <th>{lang.paymentMethod}</th>
              <th>{lang.payeeName}</th>
              <th>{lang.authorizationNumber}</th>
              <th>{lang.dealership}</th>
              <th>{lang.createdAt}</th>
              <th>{lang.total}</th>
              <th data-actions>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: claimJobPaymentItem }) => {
                const isSelected = selectedItems?.find((it) => it.id === claimJobPaymentItem.id);

                return (
                  <React.Fragment key={claimJobPaymentItem.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <Button
                          data={claimJobPaymentItem}
                          onClick={toggleItem}
                          class={ThemeButtonCircle}
                          media={<Icon class={isSelected ? 'fas-check-square' : 'far-square'} />}
                          disabled={!permissions.CREATE_CLAIMJOBPAYMENT}
                          htmlElementProps={{
                            title: !permissions.CREATE_CLAIMJOBPAYMENT
                              ? lang.unauthorized
                              : isSelected
                                ? lang.unselect
                                : lang.select,
                          }}
                        />
                      </td>
                      <td>
                        <b>{claimJobPaymentItem.jobNumber}</b>
                      </td>
                      <td>{translate(claimJobPaymentItem.paymentMethodCode)}</td>
                      <td>{claimJobPaymentItem.claimJobItemPayee?.displayInfo?.title}</td>
                      <td>{claimJobPaymentItem.claimJob?.authorizationNumber}</td>
                      <td>{claimJobPaymentItem.claimJob?.claim?.dealer?.name}</td>
                      <td>
                        {claimJobPaymentItem.createdBy?.fullName && (
                          <>
                            {claimJobPaymentItem.createdBy.fullName} {lang.at}
                            <br />
                          </>
                        )}
                        {claimJobPaymentItem.createdAtLocal
                          ? localizeDate(claimJobPaymentItem?.createdAtLocal, defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        <b>
                          <FormatValue value={`${(claimJobPaymentItem.total || 0).toFixed(2)}$`} />
                        </b>
                      </td>
                      <td>
                        <Button
                          class={ThemeButtonCircle}
                          media={<Icon class="fas-info-circle" />}
                          onClick={pageRouter.updateParams}
                          data={{
                            id: claimJobPaymentItem.id.toString(),
                            action: FinanceClaimJobPaymentItemInfoTab.INFO,
                          }}
                          htmlElementProps={{ title: lang.info }}
                          active
                        />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      }
      footer={
        <Pagination
          class={ThemePaginationRounded}
          count={listApi.payload?.count}
          offset={listApi.body?.offset}
          limit={listApi.body?.limit}
          onLimitChange={(limit) => listApi.execute((body) => ({ ...body, limit, offset: 0 }))}
          onOffsetChange={(offset) => listApi.execute((body) => ({ ...body, offset }))}
        />
      }
    />
  );
}
